/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy meant touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in one hour. Used by the foreground reminders window. */
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (even though it currently matches the
 * offline-cache value) because it is an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time cap for session check in route `beforeLoad` guards.
 *
 * P23/CC-60 · I1-bis. This value lived as `const SESSION_CHECK_TIMEOUT = 1500`
 * INSIDE `_app/route.tsx`, which is why sibling guard `_auth/route.tsx` —which
 * makes the exact same call— was left without a cap: with `get-session`
 * HUNG (held, neither 401 nor 5xx, which resolve fast) `beforeLoad` did not
 * resolve, the router did not mount the route and `/login` stayed with
 * `document.body.innerText` EMPTY at 3/6/10/15/20 s — measured here on dev
 * server and also on production bundle.
 *
 * It lives here, shared, precisely so the defense cannot again
 * remain in a single guard: once past the cap the screen is RENDERED and the late
 * response still synchronizes machine + cache (`BootManager` redirects later
 * if it turned out there was a session).
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (detail title and notes,
 * subtasks). Balances typing fluidity against the number of mutations.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably longer than the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
